import React, { useEffect, useRef, useState } from "react";
import { Col, Row } from "antd";
import { downloadUserKpiReport } from "../../../service/ReportingService";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import CustomTab from "../../../components/shared/tab/TabComponent";
import searchIconsvg from "../../../assets/icons/Search-Icon.svg";
import usePagination from "../../../assets/hooks/PaginationHooks/usePagination";
import useUserKpiReport from "../../../hooks/useUserKpiReport";
import CustomButton from "../../../components/global/Button/CustomButton";
import CustomInput from "../../../components/global/CustomInput/CustomInput";
import { UserReportColumns } from "../../../assets/FormFeilds/UserReportColumn";
import AdminHOC from "../../../components/shared/HOC/AdminHOC";
import CommonTable from "../../../components/shared/table/Table";
import LottieIcon from "../../../components/shared/lottieIcon/LottieIcon";
import { showErrorMessage, showSuccessMessage } from "../../../components/shared/pagination/messages";
import PageLoader from "../../../components/global/Loader/PageLoader";
import { tabList } from "../../../constants/constants";


const UserReportPage = () => {
  const tableRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultStatus = searchParams.get("status") || "active";

  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isInputHovered, setIsInputHovered] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [status, setStatus] = useState(defaultStatus);
  const isActive = status === "active";

  const {
    currentPage,
    pageSize,
    setCurrentPage,
    totalElements,
    setTotalElements,
  } = usePagination();

  const { data, fetchUserKpiReport } = useUserKpiReport();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetchUserKpiReport(currentPage, pageSize, isActive, searchText);
      if (response) {
        setTotalElements(response.data.totalElements || 0);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value.toLowerCase());
    setCurrentPage(1);
  };

  const handleTableChange = (pagination) => {
    if (pagination) {
      if (pagination.pageSize !== pageSize) {
        setCurrentPage(1);
      } else {
        setCurrentPage(pagination.current);
      }
      const scrollableNode = tableRef.current?.querySelector(".ant-table-body");
      if (scrollableNode) {
        scrollableNode.scrollTo({ top: 0, behavior: "auto" });
      }
    }
  };

  const handleViewUserClick = (record) => {
    navigate(`/users/user-history/${record.userId}?name=${encodeURIComponent(record.fullName)}`)
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      const query = searchText?.trim() || "";
      fetchData(currentPage, pageSize, isActive, query);
    }, 500);
    return () => clearTimeout(debounce);
  }, [currentPage, pageSize, isActive, searchText]);

  const downloadReport = async () => {
    setShowLoader(true);
    try {
      await downloadUserKpiReport(isActive, searchText);
      showSuccessMessage("Report Downloaded Successfully!");
    } catch (error) {
      showErrorMessage(error?.message || "Failed to Download Report");
    } finally {
      setShowLoader(false);
    }
  };

  const handleTabChange = (key) => {
    setStatus(key);
    setCurrentPage(1);
    setSearchText("");
  };

  return (
    <>
      <PageLoader visible={showLoader} />
      <section className="w-[100%] max-w-full h-[95vh] p-6 flex flex-col border border-[#E8E8E8] rounded-[10px] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.05)]">
        <header className="flex flex-col gap-6 mb-6">
          <div className="flex justify-between items-center h-[50px]">
            <h1 className="text-[#E74860] text-[1.75rem] font-normal">
              All Employees Report
            </h1>
          </div>
        </header>

        <Row>
          <Col span={6}>
            <CustomTab
              entries={tabList}
              activeKey={status}
              onChange={handleTabChange}
            />
          </Col>
          <Col span={18}>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <div
                onMouseEnter={() => setIsInputHovered(true)}
                onMouseLeave={() => setIsInputHovered(false)}
              >
                <CustomInput
                  key={status}
                  icon={
                    isInputHovered ? (
                      <LottieIcon name="searchIcon" size={24} shouldPlay={true} />
                    ) : (
                      <img src={searchIconsvg} alt="search" className="w-6 h-6" />
                    )
                  }
                  className="!w-[250px]"
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search employee"
                  allowClear
                />
              </div>
              <CustomButton
                onClick={downloadReport}
              >
                Export
              </CustomButton>
            </div>
          </Col>
        </Row>

        <div ref={tableRef}>
          <CommonTable
            data={data}                                        
            columns={UserReportColumns(handleViewUserClick)}   
            showLoader={loading}
            className="hide-scrollbar top-table-radius"
            scroll={{ y: "60vh" }}
            rowClickable={true}
            settingModalDataFunction={handleViewUserClick}
            hover={true}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalElements,
              showSizeChanger: true,
            }}
            onChange={handleTableChange}                     
            rowKey={(record) => record.userId}
            serverSide={true}
          />
        </div>
      </section>

    </>
  );
};

export default AdminHOC(UserReportPage);
